import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowForward } from '@mui/icons-material';
import { tok, LABEL, weeks } from './clockUi';
import DepthBadge from '../DepthBadge';
import { sideOf } from './clockSide';

// The focused market's read beside the Cycle Clock (DWLF-301): its window, place in the cycle and
// daily pivot, from the API's figures (absent shows as —), in the words of the side the market was read for
// (lows or highs, DWLF-552).
const fmtDate = (t) => (Number.isFinite(t) ? new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—');
const fmtPrice = (v) => (Number.isFinite(v) ? v.toLocaleString('en-GB', { maximumFractionDigits: v >= 100 ? 2 : 4 }) : '—');

function Stat({ label, children, wide = false }) {
  return (
    <Box sx={{ backgroundColor: tok('surface'), p: '14px 16px', display: 'flex', flexDirection: 'column', gap: 0.75, ...(wide ? { gridColumn: '1 / -1' } : {}) }}>
      <Typography component="span" sx={{ ...LABEL, fontSize: '0.625rem', color: tok('ink-50') }}>{label}</Typography>
      <Typography component="span" sx={{ fontSize: '0.875rem', color: tok('text') }}>{children}</Typography>
    </Box>
  );
}

export default function MarketRead({ focus }) {
  if (!focus) return null;
  const side = sideOf(focus.side);
  const daily = focus.setup?.daily;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600, fontSize: '2rem', letterSpacing: '-0.02em', color: tok('text') }}>{focus.symbol}</Typography>
        <Typography component="span" sx={{ ...LABEL, fontSize: '0.6875rem', px: 1.25, py: 0.9, border: `1px solid ${tok(focus.stage.token)}`, color: tok(focus.stage.token) }}>{focus.stage.label}</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1px', backgroundColor: tok('ink-08'), border: `1px solid ${tok('ink-08')}` }}>
        <Stat label={`Last confirmed ${side.pivot}`}>{focus.window?.ruler ? `${fmtDate(focus.window.ruler.pivotTime)} · ${fmtPrice(focus.window.ruler.price)}` : '—'}</Stat>
        <Stat label="Week of cycle">{focus.onDial ? `${Math.round(focus.weeksSincePivot)} of ~${weeks(focus.typicalWeeks)}${Number.isFinite(focus.gapCount) ? ` (${focus.gapCount} gaps)` : ''}` : '—'}</Stat>
        <Stat label={`Next ${side.pivot} window`}>{focus.window?.opens ? `${fmtDate(focus.window.opens.time)} → ${fmtDate(focus.window.closes?.time)}` : '—'}</Stat>
        <Stat label={`Daily ${side.pivot} pivot`}>
          {daily?.status
            ? `${daily.status}${Number.isFinite(daily.confidencePct) ? ` · ${daily.confidencePct}%` : ''}${Number.isFinite(daily.pivotPrice) ? ` · ${fmtPrice(daily.pivotPrice)}` : ''}`
            : 'None in window'}
        </Stat>
        <Stat label="Gap spread">{focus.spreadPct != null ? `${Math.round(focus.spreadPct)}% of the typical gap` : '—'}</Stat>
        <Stat label="From pivot">{Number.isFinite(focus.setup?.price?.pctFromPivot) ? `${focus.setup.price.pctFromPivot > 0 ? '+' : ''}${focus.setup.price.pctFromPivot.toFixed(1)}%` : '—'}</Stat>
        <Stat label={side.depthLabel} wide><DepthBadge depth={focus.depth} side={side.id} /></Stat>
      </Box>
      <ButtonBase
        component={RouterLink}
        to={`/markets/${focus.symbol}`}
        sx={{ ...LABEL, alignSelf: 'flex-end', height: 40, px: 2, gap: 1, fontSize: '0.6875rem', backgroundColor: tok('accent'), color: tok('on-accent') }}
      >
        Open chart <ArrowForward sx={{ fontSize: '0.9rem' }} />
      </ButtonBase>
    </Box>
  );
}
